import { ConversationMessageContent } from '@modules/conversation-message/ConversationMessageContent';
import { useEditor } from '@modules/editor';
import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useMemo } from 'react';
import { ChatMessageListItem, IChatConversation } from 'src/@types/chat';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useSelector } from 'src/redux/store';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';

export const ThreadIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  lineHeight: 1,
  color: theme.palette.primary.main,
  width: theme.spacing(6),
  height: theme.spacing(6),
  '& .iconify': {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
}));

const ChatMessageContentStyled = styled(ConversationMessageContent)(() => ({
  display: 'flex',
  width: 'auto',
  fontSize: '1.5rem',
  fontWeight: '700',
  maxWidth: '100%',

  '& div > div > p': {
    display: '-webkit-box',
    WebkitLineClamp: 1,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    lineHeight: '1.5',
    maxHeight: '3em',
    wordWrap: 'break-word',
  },
}));

type Props = {
  conversation: IChatConversation;
  messages?: ChatMessageListItem[];
};

export const ThreadFirstMessage = ({ conversation, messages }: Props) => {
  const { editorConfig } = useChatConversationContext();
  const { editor, editorLoaded } = useEditor(editorConfig);
  const { translate } = useLocales();
  const usersById = useSelector((state) => state.workspace.usersById);

  const parentMessage = useMemo(() => {
    if (!messages?.length) return null;
    return messages.find(
      (message) => message._id === conversation.parentMessageId,
    );
  }, [messages, conversation]);

  const threadOwner = useMemo(() => {
    if (!parentMessage?.author || !conversation?.createdBy) return null;
    return usersById[conversation?.createdBy || parentMessage.author];
  }, [parentMessage, usersById, conversation]);

  if (!parentMessage || !editorLoaded) return null;

  return (
    <Stack
      justifyContent="flex-end"
      flexGrow={1}
      sx={{ pt: 4, px: 3, pb: 2, maxWidth: '100%', overflow: 'hidden' }}
      spacing={0.5}
    >
      <ThreadIcon>
        <Iconify icon="tabler:timeline-event-text" />
      </ThreadIcon>

      <ChatMessageContentStyled message={parentMessage} editor={editor} />
      {!!threadOwner && (
        <Stack
          direction="row"
          alignItems="center"
          color="text.secondary"
          gap={0.5}
        >
          <Typography>{translate('communicate.thread.started_by')}</Typography>
          <UserProfileShortPopoverTrigger userId={threadOwner._id}>
            <Typography variant="subtitle1" color="primary">
              {threadOwner.name}
            </Typography>
          </UserProfileShortPopoverTrigger>
        </Stack>
      )}
    </Stack>
  );
};
